import React from 'react';
import {
  Box,
  ListSubheader,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageEditor from 'application/routes/config/components/MessageEditor';
import { camelCaseToTitleCase } from 'application/routes/config/utils/stringConversion';
import {
  StyledTextField,
  FieldLabel,
  SecondaryButton,
} from './styledFormComponents';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import FormSection from 'application/components/FormSection/FormSection';
import featureFlag from 'application/common/featureFlag';

interface AppointmentSMSTriggerProps {
  index: number;
  trigger: any;
  setFeaturesConfig: (callback: (prev: any) => any) => void;
  featuresConfig: any;
  smsVariables?: any[];
}

export default function AppointmentSMSTrigger({
  index,
  trigger,
  setFeaturesConfig,
  featuresConfig,
  smsVariables = [],
}: AppointmentSMSTriggerProps) {
  const hasSmsVariables =
    Array.isArray(smsVariables) && smsVariables.length > 0;

  const targetOptions = [
    { key: 'customer', label: 'Customer' },
    ...(featureFlag('noSetterCloser')
      ? []
      : [{ key: 'closer', label: 'Closer' }]),
  ];

  const notificationTypes = [
    {
      key: 'reminder',
      label: 'Reminder',
    },
    {
      key: 'statusChange',
      label: 'Status Change',
    },
  ];

  const appointmentStatuses = [
    {
      key: 'scheduled',
      label: 'Scheduled',
    },
    {
      key: 'rescheduled',
      label: 'Rescheduled',
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
    },
  ];

  // Handler for message changes from MessageEditor
  const handleMessageChange = (newMessage: string) => {
    const triggers =
      featuresConfig?.schedule?.appointmentNotifications?.triggers || [];
    const updatedTriggers = [...triggers];
    updatedTriggers[index] = {
      ...updatedTriggers[index],
      message: newMessage,
    };

    setFeaturesConfig((prev: any) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        appointmentNotifications: {
          ...prev.schedule?.appointmentNotifications,
          triggers: updatedTriggers,
        },
      },
    }));
  };

  const updateTrigger = (field: string, value: any) => {
    const triggers =
      featuresConfig?.schedule?.appointmentNotifications?.triggers || [];
    const updatedTriggers = [...triggers];
    updatedTriggers[index] = {
      ...updatedTriggers[index],
      [field]: value,
    };

    setFeaturesConfig((prev: any) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        appointmentNotifications: {
          ...prev.schedule?.appointmentNotifications,
          triggers: updatedTriggers,
        },
      },
    }));
  };

  const handleDelete = () => {
    const triggers =
      featuresConfig?.schedule?.appointmentNotifications?.triggers || [];
    const updatedTriggers = [...triggers];
    updatedTriggers.splice(index, 1);

    setFeaturesConfig((prev: any) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        appointmentNotifications: {
          ...prev.schedule?.appointmentNotifications,
          triggers: updatedTriggers,
        },
      },
    }));
  };

  const getTriggerSummary = () => {
    const enabled = trigger.enabled ? '✓' : '✗';
    const target = trigger.target === 'closer' ? 'Closer' : 'Customer';
    const type =
      trigger.type === 'reminder'
        ? 'Reminder'
        : trigger.type === 'statusChange'
        ? 'Status Change'
        : trigger.type || 'Unknown';
    const messagePreview = trigger.message?.slice(0, 50) || 'No message set';
    return `${enabled} ${target} ${type} - ${messagePreview}${
      trigger.message?.length > 50 ? '...' : ''
    }`;
  };

  return (
    <Accordion
      sx={(theme) => ({
        '&:before': {
          display: 'none',
        },
        boxShadow: 'none',
        border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
        borderRadius: '12px',
        mb: 2,
        backgroundColor: 'transparent',
        '& .MuiAccordionSummary-root': {
          backgroundColor: theme.custom?.background2 || '#F5F5F5',
          borderRadius: '12px',
          minHeight: 56,
          '&.Mui-expanded': {
            minHeight: 56,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
        '& .MuiAccordionDetails-root': {
          backgroundColor: theme.custom?.background1 || '#FFFFFF',
          borderTop: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
          padding: 3,
        },
      })}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={(theme) => ({ color: theme.custom?.text })} />
        }
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            overflow: 'hidden',
            pr: 2,
          }}
        >
          <Typography
            sx={{
              maxWidth: '90%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 14,
              fontWeight: 500,
              color: (theme) => theme.custom?.text || '#03252E',
            }}
          >
            {getTriggerSummary()}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%',
          }}
        >
          {/* Enabled Toggle */}
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <Typography
                sx={(theme) => ({
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.custom?.text,
                })}
              >
                Enabled
              </Typography>
            </Box>
            <ToggleSwitch
              checked={trigger.enabled || false}
              onChange={(checked: boolean) => {
                updateTrigger('enabled', checked);
              }}
            />
          </FormSection>

          {/* Target Selection */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FieldLabel>Target</FieldLabel>
              <Select
                fullWidth
                value={trigger.target === 'closer' ? 'closer' : 'customer'}
                onChange={(e) => {
                  updateTrigger(
                    'target',
                    e.target.value === 'closer' ? 'closer' : ''
                  );
                }}
                sx={(theme) => ({
                  backgroundColor: theme.custom?.background2 || '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                    borderWidth: '1px',
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 14px',
                    color: (theme) => theme.custom?.text,
                  },
                })}
                MenuProps={{
                  PaperProps: {
                    sx: (theme) => ({
                      maxHeight: 200,
                      backgroundColor: theme.custom?.background2 || '#F5F5F5',
                      border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: theme.custom?.text,
                      },
                    }),
                  },
                }}
              >
                {targetOptions.map((option) => (
                  <MenuItem key={`target-${option.key}`} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Phone Number Field (only show if target is not closer) */}
            {trigger.target !== 'closer' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FieldLabel>Phone Number Field</FieldLabel>
                <Select
                  fullWidth
                  value={
                    trigger.target && trigger.target !== 'closer'
                      ? trigger.target
                      : ''
                  }
                  onChange={(e) => {
                    updateTrigger('target', e.target.value);
                  }}
                  sx={(theme) => ({
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                      borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                      padding: '12px 14px',
                      color: (theme) => theme.custom?.text,
                    },
                  })}
                  MenuProps={{
                    PaperProps: {
                      sx: (theme) => ({
                        maxHeight: 200,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        border: `1px solid ${
                          theme.custom?.stroke || '#E0E0E0'
                        }`,
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          color: theme.custom?.text,
                        },
                        '& .MuiListSubheader-root': {
                          backgroundColor: theme.custom?.background3,
                          color: theme.custom?.text,
                        },
                      }),
                    },
                  }}
                >
                  {hasSmsVariables ? (
                    smsVariables.map((variable, varIndex) => {
                      if (variable.type === 'Contact') {
                        return [
                          <ListSubheader
                            key={`sms-header-${variable.id || varIndex}`}
                          >
                            {variable.heading}
                          </ListSubheader>,
                          ...Object.keys(variable.contactFields)
                            .filter((key) => variable.contactFields[key].show)
                            .map((key, idx) => (
                              <MenuItem
                                key={`sms-field-${
                                  variable.id || varIndex
                                }-${key}-${idx}`}
                                value={variable.name + '.' + key}
                                sx={{ paddingLeft: '36px' }}
                              >
                                {camelCaseToTitleCase(key)}
                              </MenuItem>
                            )),
                        ];
                      }
                      return (
                        <MenuItem
                          key={`sms-var-${
                            variable.id || `${varIndex}-${variable.name}`
                          }`}
                          value={variable.name}
                        >
                          {variable.label}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled value="">
                      No phone number fields available
                    </MenuItem>
                  )}
                </Select>
              </Box>
            )}
          </Box>

          {/* Type and Conditional Fields */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FieldLabel>Type</FieldLabel>
              <Select
                fullWidth
                value={trigger.type || 'reminder'}
                onChange={(e) => {
                  const newType = e.target.value;
                  const updatedTrigger: any = {
                    ...trigger,
                    type: newType,
                  };
                  if (newType === 'reminder') {
                    delete updatedTrigger.status;
                  } else if (newType === 'statusChange') {
                    delete updatedTrigger.minutesBefore;
                  }
                  updateTrigger('type', newType);
                  if (newType === 'reminder' && trigger.status) {
                    updateTrigger('status', undefined);
                  } else if (
                    newType === 'statusChange' &&
                    trigger.minutesBefore
                  ) {
                    updateTrigger('minutesBefore', undefined);
                  }
                }}
                sx={(theme) => ({
                  backgroundColor: theme.custom?.background2 || '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                    borderWidth: '1px',
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 14px',
                    color: (theme) => theme.custom?.text,
                  },
                })}
                MenuProps={{
                  PaperProps: {
                    sx: (theme) => ({
                      maxHeight: 200,
                      backgroundColor: theme.custom?.background2 || '#F5F5F5',
                      border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: theme.custom?.text,
                      },
                    }),
                  },
                }}
              >
                {notificationTypes.map((option) => (
                  <MenuItem
                    key={`notification-${option.key}`}
                    value={option.key}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Minutes Before (for reminder type) */}
            {trigger.type === 'reminder' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FieldLabel>Send Reminder (minutes before)</FieldLabel>
                <StyledTextField
                  type="number"
                  fullWidth
                  value={trigger.minutesBefore || ''}
                  onChange={(e) => {
                    updateTrigger(
                      'minutesBefore',
                      parseInt(e.target.value, 10) || 0
                    );
                  }}
                  sx={(theme) => ({
                    '& .MuiInputBase-input': {
                      color: theme.custom?.text,
                    },
                  })}
                />
              </Box>
            )}

            {/* Status (for statusChange type) */}
            {trigger.type === 'statusChange' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FieldLabel>Status</FieldLabel>
                <Select
                  fullWidth
                  value={trigger.status || 'scheduled'}
                  onChange={(e) => {
                    updateTrigger('status', e.target.value);
                  }}
                  sx={(theme) => ({
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                      borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                      padding: '12px 14px',
                      color: (theme) => theme.custom?.text,
                    },
                  })}
                  MenuProps={{
                    PaperProps: {
                      sx: (theme) => ({
                        maxHeight: 200,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        border: `1px solid ${
                          theme.custom?.stroke || '#E0E0E0'
                        }`,
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          color: theme.custom?.text,
                        },
                      }),
                    },
                  }}
                >
                  {appointmentStatuses.map((option) => (
                    <MenuItem key={`status-${option.key}`} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>

          {/* Message Editor */}
          <Box>
            <MessageEditor
              message={trigger.message || ''}
              onChange={handleMessageChange}
              smsVariables={smsVariables}
            />
          </Box>

          {/* Delete Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 1,
            }}
          >
            <SecondaryButton
              onClick={handleDelete}
              sx={(theme) => ({
                backgroundColor: 'transparent',
                color: theme.custom?.primary || '#FF6A00',
                border: `1px solid ${theme.custom?.primary || '#FF6A00'}`,
                maxWidth: 200,
                '&:hover': {
                  backgroundColor: theme.custom?.primaryLight || '#FAC8B2',
                  color: theme.custom?.textCTA || '#FFFFFF',
                },
              })}
            >
              Delete Trigger
            </SecondaryButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
